import { useTranslation } from 'react-i18next';
import { Typography } from '@/components';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Step = {
  label: string;
  key: string;
};

const STEPS: Step[] = [
  { key: 'eoi_received', label: 'EOI Received' },
  { key: 'nda_review', label: 'NDA Review' },
  { key: 'vdr_review', label: 'VDR Review' },
  { key: 'preparing_nbo', label: 'Preparing NBO' },
  { key: 'preparing_bo', label: 'Preparing BO' },
  { key: 'preparing_signing', label: 'Preparing Signing' },
  { key: 'deal_closing', label: 'Deal Closing' },
];

type OfferTransactionProgressProps = {
  readonly progress?: string;
};

export function TransactionProgress({ progress }: OfferTransactionProgressProps) {
  const { t } = useTranslation(['offers']);

  const currentIndex = STEPS.findIndex((step) => step.key === progress);

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>
          <Typography as="h3" weight="semibold" size="title" className="uppercase">
            {t('Transaction Progress')}
          </Typography>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="overflow-x-auto pb-2">
          <div className="min-w-[560px]">
            <div className="relative mb-3 w-full">
              <div
                className="absolute top-2 z-0 h-1 bg-gray-200"
                style={{
                  left: `calc(100% / ${STEPS.length * 2})`,
                  right: `calc(100% / ${STEPS.length * 2})`,
                }}
              />
              <div
                className="absolute top-2 z-0 h-1 bg-green-700 transition-all"
                style={{
                  left: `calc(100% / ${STEPS.length * 2})`,
                  width: currentIndex > 0 ? `calc((100% / ${STEPS.length}) * ${currentIndex})` : '0%',
                }}
              />
              <div className="relative z-0 flex justify-between">
                {STEPS.map((_, index) => {
                  const isActive = index <= currentIndex;
                  return (
                    <div key={index} className="flex flex-1 justify-center">
                      <div
                        className={`h-5 w-5 rounded-full border-2 transition-all duration-300 ${
                          isActive ? 'border-green-700 bg-green-700' : 'border-gray-300 bg-white'
                        }`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between text-xs font-medium text-gray-700">
              {STEPS.map((step) => (
                <div key={step.key} className="flex flex-1 justify-center px-1">
                  <Typography as="p" weight="normal" size="body-2" className="text-center">
                    {step.label}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-2 flex items-center gap-1 text-xs text-gray-400 lg:hidden">{'<-> Scroll to see all steps'}</p>
      </CardContent>
    </Card>
  );
}
